// App.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Login from "./auth/login/Login";
import DashboardAdmin from "./pages/AdminDashboard";
import UserHomepage from "./pages/Homepage/UserHomepage";
import UserForm from "./pages/ProviderForm/UserForm";
import FormularioPublico from "./pages/ProviderForm/FormularioPublico";
import ProveedorLayout from "./components/ProveedorLayout";
import { AuthProvider, useAuth } from "./AuthContext";
import { ProtectedRoute } from './ProtectedRoute';
import { useInactivityLogout } from './hooks/useInactivityLogout';
import InactivityWarning from './components/InactivityWarning/InactivityWarning';
import { CircularProgress, Box } from '@mui/material';
import Profile from "./auth/profile/profile";
import RevisorDashboard from "./pages/RevisorDashboard";
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

function AppContent() {
  const { user, login, logout, loading: authLoading } = useAuth();
  const { showWarning, timeLeft, stayLoggedIn, handleLogout, onSuccessfulRequest } = useInactivityLogout();

  useEffect(() => {
    const handleApiSuccess = () => onSuccessfulRequest();
    window.addEventListener('api-request-success', handleApiSuccess);
    return () => window.removeEventListener('api-request-success', handleApiSuccess);
  }, [onSuccessfulRequest]);

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const getRedirectPath = (userRole) => {
    switch (userRole) {
      case "superusuario":
      case "administrador": return "/administrador";
      case "revisor": return "/revisor";
      case "proveedor": return "/form";
      default: return "/login";
    }
  };

  return (
    <>
      <Routes>
        <Route path="/" element={
          <Navigate to={user ? getRedirectPath(user.rol) : "/login"} replace />
        } />

        <Route path="/formulario-general/:link_unico" element={<FormularioPublico />} />

        <Route path="/login" element={
          !user
            ? <Login onLogin={login} />
            : <Navigate to={getRedirectPath(user.rol)} replace />
        } />

        <Route
          path="/administrador/*"
          element={
            <ProtectedRoute roles={["superusuario", "administrador"]}>
              <DashboardAdmin onLogout={handleLogout} username={user?.nombre} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/"
          element={
            <ProtectedRoute roles={["proveedor"]}>
              <ProveedorLayout onLogout={handleLogout} username={user?.nombre} />
            </ProtectedRoute>
          }
        >
          <Route path="/form" element={<UserHomepage />} />
          <Route path="/form/:type/:step" element={<UserForm />} />
          <Route path="/perfil" element={<Profile />} />
        </Route>

        <Route
          path="/revisor/*"
          element={
            <ProtectedRoute roles={["revisor"]}>
              <RevisorDashboard onLogout={handleLogout} username={user?.nombre} />
            </ProtectedRoute>
          }
        />

        <Route
          path="*"
          element={<Navigate to={user ? getRedirectPath(user.rol) : "/login"} replace />}
        />
      </Routes>

      {showWarning && user && (
        <InactivityWarning
          timeLeft={timeLeft}
          onStayLoggedIn={stayLoggedIn}
        />
      )}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;